"use client";

import { useAuiState } from "@assistant-ui/react";
import { ChevronDownIcon, GlobeIcon } from "lucide-react";
import { type FC, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/molecules/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/molecules/tooltip";
import {
  type CitedSource,
  citedSources,
  faviconUrl,
  hostLabel,
  type SourceLike,
  sourcesLabel,
  stackSources,
} from "@/lib/citations";
import { cn } from "@/lib/utils";

/** The message's web sources, in citation order. Stable across renders while streaming. */
function useMessageSources(): CitedSource[] {
  const parts = useAuiState((s) => s.message.parts) as readonly SourceLike[];
  return citedSources(parts);
}

function Favicon({ url, className }: { url: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const src = faviconUrl(url);
  if (!src || failed) {
    return <GlobeIcon aria-hidden className={cn("text-muted-foreground shrink-0", className)} />;
  }
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className={cn("shrink-0 rounded-sm", className)}
    />
  );
}

/** An inline [n] citation: a small chip with the site's favicon and name. */
export const CitationChip: FC<{ n: string }> = ({ n }) => {
  const source = useMessageSources().find((s) => s.n === n);
  // A number the model made up, or a source still on its way: show the plain marker.
  if (!source) return <span className="text-muted-foreground">[{n}]</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          data-slot="citation-chip"
          aria-label={`Source ${n}: ${source.title}`}
          className="bg-muted text-muted-foreground hover:bg-accent hover:text-foreground mx-0.5 inline-flex max-w-40 translate-y-[-1px] items-center gap-1 rounded-full px-1.5 py-px align-middle text-[11px] leading-4 font-medium no-underline transition-colors"
        >
          <Favicon url={source.url} className="size-3" />
          <span className="truncate">{hostLabel(source.url)}</span>
        </a>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">
        <span className="font-medium">{source.title}</span>
        <span className="block opacity-70">{hostLabel(source.url)}</span>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * The answer's sources, folded into one small button in the message footer: a stack of
 * favicons and "8 sources". It opens a popover with the full list, so the sources never add
 * height to the answer. The inline chips still show which site backs each sentence.
 * Renders nothing when the answer cites none.
 */
export const MessageSources: FC = () => {
  const sources = useMessageSources();
  const [open, setOpen] = useState(false);
  if (sources.length === 0) return null;
  const label = sourcesLabel(sources.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="message-sources"
          aria-label={`${label}, show list`}
          className="text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground group -ms-1 me-1 inline-flex h-7 items-center gap-1.5 rounded-full ps-1 pe-2 text-xs transition-colors"
        >
          <span className="flex" aria-hidden>
            {stackSources(sources).map((source) => (
              <span
                key={source.n}
                className="bg-muted ring-background group-hover:ring-muted group-data-[state=open]:ring-muted grid size-4 place-items-center overflow-hidden rounded-full ring-2 not-first:-ms-1"
              >
                <Favicon url={source.url} className="size-4 rounded-full" />
              </span>
            ))}
          </span>
          <span className="tabular-nums">{label}</span>
          <ChevronDownIcon
            aria-hidden
            className="size-3 transition-transform group-data-[state=open]:rotate-180"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 max-w-[calc(100vw-2rem)] rounded-xl p-1.5"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ol aria-label="Sources" className="max-h-80 overflow-y-auto">
          {sources.map((source) => (
            <li key={source.n}>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-muted focus-visible:bg-muted grid grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm outline-none"
              >
                <Favicon url={source.url} className="size-4" />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="text-foreground truncate">{source.title}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {hostLabel(source.url)}
                  </span>
                </span>
                <span className="text-muted-foreground font-mono text-[11px] tabular-nums">
                  {source.n}
                </span>
              </a>
            </li>
          ))}
        </ol>
      </PopoverContent>
    </Popover>
  );
};
